// @flow
import * as React from 'react';
import _ from 'lodash';
import StyledTitle from '../hoc/StyledTitle';

import './TextWithEllipsis.css';
import ToolTip from './AssetPropertyView';

const debug = require('../debug/debug')('wc:common:TextWithEllipsis');

const ELLIPSIS = '…';

type Props = {
  text: string,
  className: string,
  style?: Object,
  textStyle?: Object,
  noTooltip?: boolean,
  responsive?: boolean
};

type State = {
  text: string
};

@StyledTitle
export default class TextWithEllipsis extends React.Component<Props, State> {
  _node: ?HTMLDivElement;
  _lastWidth: number = 0;
  _changeCount: number = 0;

  static defaultProps = {
    textStyle: {
      color: 'var(--primary-font-color)',
      fontSize: '14px'
    },
    responsive: true,
    className: ''
  };

  state = {
    text: this.props.text
  };

  componentDidMount() {
    this.checkSizes();

    if (!this.props.responsive) {
      window.addEventListener('resize', this.debouncedReset);
    }
  }

  componentDidUpdate(prevProps: Props) {
    const diff = this._node ? Math.abs(this._lastWidth - this._node.clientWidth) : 0;

    if (prevProps.text !== this.props.text) {
      this.reset();
    } else {
      if (prevProps != this.props) {
        if (this.props.responsive && diff > 5) {
          this.debouncedReset();
        } else {
          this.checkSizes();
        }
      }
    }

    if (diff > 5) {
      this._lastWidth = this._node.clientWidth;
    }
  }

  componentWillUnmount() {
    if (!this.props.responsive) {
      window.removeEventListener('resize', this.debouncedReset);
    }
  }

  reset = () => {
    if (!this._node) {
      return;
    }

    // debug('[reset()] config: ', {
    //   propsText: this.props.text,
    //   stateText: this.state.text,
    //   clientWidth: this._node.clientWidth,
    //   _lastWidth: this._lastWidth,
    //   _changeCount: this._changeCount
    // });

    this._changeCount = 0;
    this.setState({ text: this.props.text });
  };

  debouncedReset = _.debounce(this.reset, 25);

  checkSizes = () => {
    if (!this._node) {
      return;
    }

    if (this._node.scrollWidth <= this._node.clientWidth && !this.props.wrap) {
      return;
    }

    if (this._changeCount > 250) {
      return;
    }

    if (!this.state.text) return;
    const s1 = this.state.text.replace(ELLIPSIS, '');
    const arr = s1.split('.');
    if (arr.length > 1) {
      // Pop off the last element to exclude filename extensions from ellipsis logic.
      arr.pop();
    }
    const s2 = arr.join('.');

    if (s2.length < 3) {
      return;
    }

    const i = Math.floor(s2.length / 2);
    const text = s1.substring(0, i) + ELLIPSIS + s1.substring(i + 1, s1.length);
    // debug('[checkSizes()] updating text: ', text);
    this._changeCount++;
    this.setState({ text });
  };

  render() {
    const { text, style, textStyle, noTooltip, className } = this.props;
    const mergedStyle = { ...textStyle, ...style };
    const props: Object = { name: 'TextWithEllipsis', className, style: mergedStyle, ref: (n) => (this._node = n) };
    if (!noTooltip) {
      props.title = text;
    }

    return <div {...props}>{this.state.text}</div>;
  }
}

type TextWithEllipsisFileExtensionProps = {
  text: string,
  style: Object,
  title: string,
  isFolder: string,
  extFlag: number
};

export const TextWithEllipsisFileExtension = function(props: TextWithEllipsisFileExtensionProps) {
  let text = props.text;
  let extension = null;
  let extFlag = props.extFlag ? props.extFlag : 5;
  const newprops: Object = {
    name: 'TextWithEllipsisFileExtension',
    // title: props.noTooltip ? null : props.text,
    style: { ...props.style }
  };

  if (text) {
    if (text.indexOf('/') > -1 || new RegExp('\\\\', 'g').test(text) || props.isFolder) {
      text = text.substring(0, text.length - extFlag);
      if (text != '') extension = props.text.substr(-extFlag);
      else text = props.text;
    } else {
      const arr = text.split('.');
      if (arr.length > 1) {
        extension = '.' + arr[arr.length - 1];
        arr.pop();
      }
      text = arr.join('.');
    }
  }
  let extensionWithoutDot = null,
    avoidExtension = false;
  if (extension) {
    if (extension[0] === '.') extensionWithoutDot = extension.slice(1);
    if (extensionWithoutDot) if (parseInt(extensionWithoutDot)) avoidExtension = true;
  }

  const title = props.title || null;
  if (avoidExtension) {
    text = props.text;
    extension = null;
  }

  return (
    <div {...newprops}>
      <ToolTip text={title}>
        <div className="ellipsis-container">
          <div className="ellipsis-text">{text}</div>
          <div className="ellipsis-extension">{extension ? `${extension}` : ''}</div>
        </div>
      </ToolTip>
    </div>
  );
};

export class TextWithEllipsisPathTruncate {
  extractSourceFolderFromPath(path: string) {
    let sourceFolder = '';
    for (let index = path.length - 1; index >= 0; index--) {
      if (path[index].match('\\\\') || path[index].match('/')) {
        sourceFolder = path.slice(index, path.length);
        break;
      }
    }
    return sourceFolder;
  }

  extractDirectoryFromPath(path: string, uptoLength: number) {
    let sourceDirectory = '';
    for (let index = 0; index < path.length; index++) {
      if (path[index].match('\\\\') || path[index].match('/')) {
        if (sourceDirectory.length < uptoLength || !sourceDirectory.length) {
          sourceDirectory = path.slice(0, index + 1);
        }
      }
    }
    return sourceDirectory;
  }

  static getTruncatedPath(fullpath: string) {
    const TextWithEllipsisPathTruncateObj: TextWithEllipsisPathTruncate = new TextWithEllipsisPathTruncate();
    const maxPathLimit = 40;
    let path = fullpath ? fullpath : '';
    let truncatedPath = path;
    let sourceFolder = '';
    let sourceDirectory = '';
    if (path.length > maxPathLimit) {
      sourceFolder = TextWithEllipsisPathTruncateObj.extractSourceFolderFromPath(path);
      const lengthDifference = maxPathLimit - sourceFolder.length;
      sourceDirectory = TextWithEllipsisPathTruncateObj.extractDirectoryFromPath(path, lengthDifference);
      truncatedPath = sourceDirectory + ELLIPSIS + sourceFolder;
    }
    return truncatedPath;
  }
}
